import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Plus,
  Filter,
  LayoutGrid,
  List as ListIcon,
  MoreHorizontal,
  Wrench,
  Box,
  CheckCircle2,
  Clock,
  MapPin,
  X,
  AlertCircle
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PageTitle from 'components/generic/PageTitle';
import PageHeaderBreadcrumbs from 'components/generic/Breadcrumbs';
import { debounce } from 'lib/utils';
import { getAllEquipment, getAllEquipmentRequest, acceptrejectEquipment } from 'services/equipmentService';
import AdminEquipmentRequestCard from 'components/cpEquipments/AdminEquipmentRequestCard';
import Tabs from 'components/generic/FilterTabs';


const normalizeStatus = (status) => {
  const statusMap = {
    '1': 'Available',
    '2': 'Maintenance',
    '3': 'Retired',
    '4': 'In Use',
  };
  return statusMap[status] || 'Available';
};

const StatusBadge = ({ status }) => {
  const normalizedStatus = normalizeStatus(status);

  const styles = {
    Available: 'text-muted-foreground bg-green-50 border-green-100 dark:bg-green-900/20 dark:border-green-900/30 dark:text-green-400',
    'In Use': 'text-orange-600 bg-orange-50 border-orange-100 dark:bg-orange-900/20 dark:border-orange-900/30 dark:text-orange-400',
    Maintenance: 'text-red-600 bg-red-50 border-red-100 dark:bg-red-900/20 dark:border-red-900/30 dark:text-red-400',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styles[normalizedStatus] || styles.Available}`}>
      {normalizedStatus === 'Available' && '○ Available'}
      {normalizedStatus === 'In Use' && '○ In Use'}
      {normalizedStatus === 'Maintenance' && '⚡ Maintenance'}
    </span>
  );
};

const RentReuestEquipmentPage = () => {
  // local state
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rentRequests, setRentRequests] = useState([]);
const [activeTab, setActiveTab] = useState("pending");
  // pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredEquipment, setFilteredEquipment] = useState([]);

  const [statistics, setStatistics] = useState({
    total: 0,
    available: 0,
    inUse: 0,
    maintenance: 0,
  });

  // Modal States
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchEquipment = async (pageToFetch = 1, append = false) => {
    try {
      if (append) setIsLoadingMore(true);
      else setLoading(true);

      setError(null);
      const res = await getAllEquipment(limit, pageToFetch);

      const success = res?.data?.success ?? (res?.success ?? true);
      const items = res?.data?.data ?? res?.data ?? res?.data?.items ?? [];
      const summary = res?.summary ?? {};

      if (success) {
        if (append) {
          setEquipment(prev => [...prev, ...items]);
        } else {
          setEquipment(items);
        }

        if (summary) {
          setStatistics({
            total: summary.total_equipment || 0,
            available: summary.available_equipment || 0,
            inUse: summary.in_use_equipment || 0,
            maintenance: summary.maintenance_equipment || 0,
          });
        }

        setHasMore(Array.isArray(items) ? items.length === limit : false);
      } else {
        setError(res?.data?.error || res?.error || 'Failed to fetch equipment');
      }
    } catch (err) {
      setError('Failed to fetch equipment');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchEquipmentRentRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await getAllEquipmentRequest();
      console.log("Rent Requests API Response:", res);

      const success = res?.data?.error === false || res?.success === true;
      // Based on your JSON, data is an array
      const items = res?.data?.data || res?.data || [];

      if (success) {
        setRentRequests(items);
      } else {
        setError(res?.data?.message || "Failed to fetch rent requests");
      }
    } catch (err) {
      setError("Failed to fetch rent requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEquipment(1, false);
    fetchEquipmentRentRequests();
  }, []);

  const handleSearch = debounce((value, equipmentList) => {
    const query = value.toLowerCase().trim();
    if (!query) {
      setFilteredEquipment([]);
      return;
    }
    const results = (equipmentList || []).filter((item) => {
      const name = (item.equipment_name || "").toString();
      const model = (item.model_number || "").toString();
      return name.toLowerCase().includes(query) || model.toLowerCase().includes(query);
    });
    setFilteredEquipment(results);
  }, 300);

  // --- API HANDLER ---
  const handleStatusUpdate = async (status) => {
    if (!selectedRequest) return;
    setIsSubmitting(true);

    try {
      // THE PAYLOAD NOW USES THE CORRECT KEY: request_id
      const payload = {
        "request_id": selectedRequest.request_id,
        "admin_accept": status // 1 for Accept, 2 for Reject
      };

      console.log("Sending Payload to API:", payload);

      const res = await acceptrejectEquipment(payload);

      if (res?.data?.error === false || res?.success || res?.status === 200) {
        fetchEquipmentRentRequests(); // Refresh the list
        closeModals();
      } else {
        alert("Error: " + (res?.data?.message || "Action could not be completed"));
      }
    } catch (err) {
      console.error("API Error:", err);
      alert("A server error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const requestTabs = useMemo(() => [
  { label: "Pending Requests", value: "pending" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
], []);


  const getFilteredRequests = () => {
  if (!Array.isArray(rentRequests)) return [];

  // 1. First, filter by the Active Tab (Status)
  let filtered = [];
  switch (activeTab) {
    case "approved":
      filtered = rentRequests.filter(r => r.admin_accept === 1);
      break;
    case "rejected":
      filtered = rentRequests.filter(r => r.admin_accept === 2);
      break;
    default:
      // Includes 0 or null
      filtered = rentRequests.filter(
        r => r.admin_accept === 0 || r.admin_accept === null
      );
      break;
  }

  // 2. Then, filter by Search Term (Equipment Name or Crew Member Name)
  if (searchTerm.trim() !== "") {
    const query = searchTerm.toLowerCase();
    filtered = filtered.filter((item) => {
      const equipName = item.equipment?.equipment_name?.toLowerCase() || "";
      const crewName = item.crew_member?.full_name?.toLowerCase() || "";

      return equipName.includes(query) || crewName.includes(query);
    });
  }

  return filtered;
};

  const closeModals = () => {
    setIsAcceptModalOpen(false);
    setIsRejectModalOpen(false);
    setSelectedRequest(null);
  };

  const equipmentStats = [
    { label: 'Total Equipment', value: statistics.total.toString(), icon: Box, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20' },
    { label: 'Available', value: statistics.available.toString(), icon: CheckCircle2, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20' },
    { label: 'On Rent', value: statistics.inUse.toString(), icon: LayoutGrid, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20' },
    { label: 'Maintenance', value: statistics.maintenance.toString(), icon: Wrench, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20' },
  ];

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <PageHeaderBreadcrumbs />
      <div className="flex justify-between items-start mb-8">
        <PageTitle
          title={"Rent Requests"}
          description={"Track and manage Rental across all locations."}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {equipmentStats.map((stat, index) => (
          <div key={index} className="bg-card p-4 rounded-xl border border-border shadow-sm flex items-center justify-between">
            <div>
              <p className="text-muted-foreground text-sm font-medium mb-1">{stat.label}</p>
              <h3 className="text-2xl font-bold text-foreground">{stat.value}</h3>
            </div>
            <div className={`p-3 rounded-lg ${stat.bg}`}>
              <stat.icon className={`w-6 h-6 ${stat.color}`} />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-card p-4 rounded-xl border border-border shadow-sm mb-6">
        <div className="flex flex-col md:flex-row-reverse justify-between items-center gap-4">
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search equipment or crew..."
                className="pl-9 bg-background border-input focus:bg-background"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
      <Tabs
        tabs={requestTabs.map(t => t.label)}
        value={
          requestTabs.find(t => t.value === activeTab)?.label
        }
        onChange={(label) => {
          const selected = requestTabs.find(t => t.label === label);
          if (selected) setActiveTab(selected.value);
        }}
      />
      {loading && page === 1 && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-background"></div>
          <p className="text-muted-foreground mt-4">Loading Request...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-4 mb-6">
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {getFilteredRequests().length > 0 ? (
            getFilteredRequests().map((item, index) => (

              <AdminEquipmentRequestCard
                key={item.request_id || index}
                item={item}
                onViewDetails={(it) => console.log("View", it)}
                onApprove={
                  activeTab === "pending"
                    ? (it) => {
                      setSelectedRequest(it);
                      setIsAcceptModalOpen(true);
                    }
                    : null
                }
                onReject={
                  activeTab === "pending"
                    ? (it) => {
                      setSelectedRequest(it);
                      setIsRejectModalOpen(true);
                    }
                    : null
                }
              />
            ))
          ) : (
              <div className="col-span-full text-center text-muted-foreground py-12">
                No {activeTab} requests found
              </div>
          )}
        </div>
      )}

      {/* ACCEPT MODAL */}
      {isAcceptModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-card p-8 rounded-[2rem] shadow-2xl w-[90%] max-w-md animate-in zoom-in duration-200">
            <h2 className="text-xl font-bold text-gray-900 dark:text-foreground mb-3 leading-tight">
              Are you sure you want to accept this shoot?
            </h2>
            <p className="text-sm text-gray-400 mb-8 font-medium">
              This will add it to your calendar and notify the production team of your confirmation.
            </p>
            <div className="flex gap-4">
              <button
                onClick={closeModals}
                className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                disabled={isSubmitting}
                onClick={() => handleStatusUpdate(1)}
                className="flex-1 py-3 px-4 bg-[#22C55E] hover:bg-green-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all"
              >
                <CheckCircle2 className="w-4 h-4" />
                {isSubmitting ? "Processing..." : "Accept Shoot"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-card p-8 rounded-[2rem] shadow-2xl w-[90%] max-w-md relative animate-in zoom-in duration-200">
            <button onClick={closeModals} className="absolute top-6 right-6 text-gray-300 hover:text-gray-500">
                <X className="w-6 h-6" />
            </button>

            <div className="mb-6">
                <div className="w-12 h-12 rounded-full border border-red-100 flex items-center justify-center bg-red-50/50 mb-4">
                    <AlertCircle className="w-6 h-6 text-red-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-foreground">Decline Shoot Request?</h2>
                <p className="text-[11px] text-gray-400 mt-1 font-medium">Please let us know why you're declining this request. This helps improve future matching.</p>
            </div>

            <div className="space-y-4 mb-6">
                <h3 className="text-sm font-bold text-gray-900 dark:text-foreground">Reason for declining</h3>
                <div className="space-y-2.5">
                    {["Schedule conflict", "Equipment unavailable", "Location too far", "Rate too low", "Other"].map((reason) => (
                        <label key={reason} className="flex items-center gap-3 cursor-pointer group">
                            <input
                                type="radio"
                                name="reason"
                                className="w-5 h-5 accent-[#EF4444] border-gray-300"
                            />
                            <span className="text-sm text-gray-400 font-medium group-hover:text-gray-700 transition-colors">{reason}</span>
                        </label>
                    ))}
                </div>
            </div>

            <div className="mb-8">
                <h3 className="text-sm font-bold text-gray-900 dark:text-foreground mb-2">Additional comments (optional)</h3>
                <textarea
                    placeholder="Any additional details..."
                    className="w-full h-24 p-4 text-sm bg-[#FDFAF7] border border-orange-100/50 rounded-xl focus:outline-none dark:bg-muted resize-none"
                />
            </div>

            <div className="flex gap-4">
              <button
                onClick={closeModals}
                className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-all"
              >
                Cancel
              </button>
              <button
                disabled={isSubmitting}
                onClick={() => handleStatusUpdate(2)}
                className="flex-1 py-3 px-4 bg-[#EF4444] hover:bg-red-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all"
              >
                <CheckCircle2 className="w-4 h-4" />
                {isSubmitting ? "Processing..." : "Decline Request"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default RentReuestEquipmentPage;
